import { useMutation, useQueries, useQuery, useQueryClient, } from "@tanstack/react-query";
import { api, djRestAuth } from "./api/api";
import { USE_RETRIEVE_ADDABLE_TEAMS } from "./api/LeagueApiProvider";
import { USE_RETRIEVE_PLAYER_FEED, USE_RETRIEVE_TEAM_FEED, } from "./api/queryKeys";
export const STALE_TIME = 1000 * 60 * 5;
export const CACHE_TIME = 1000 * 60 * 30;
const USE_RETRIEVE_USER_PLAYER_PERMISSIONS = "useRetrieveUserPlayerPermissions";
const USE_RETRIEVE_USER_TEAM_PERMISSIONS = "useRetrieveUserTeamPermissions";
const USE_RETRIEVE_USER_PRIVACY_CONTROLS = "useRetrieveUserPrivacySettings";
const USE_RETRIEVE_TEAM = "useRetrieveTeam";
const USE_RETRIEVE_TEAM_PREFERENCES = "useRetrieveTeamPreferences";
export const USE_LIST_PLAYERS_BY_TEAM_ID = "useListPlayersByTeamId";
const USE_LIST_ADDABLE_PLAYERS = "useListAddablePlayers";
const USE_SEARCH_PLAYERS = "useSearchPlayers";
const USE_SEARCH_TEAMS = "useSearchTeams";
export const USE_RETRIEVE_INTRA_SQUAD_MATCH = "useRetrieveIntraSquadMatch";
const USE_RETRIEVE_PLAYER = "useRetrievePlayer";
const USE_RETRIEVE_PLAYER_BY_USER_ID = "useRetrievePlayerByUserId";
const USE_RETRIEVE_PLAYER_BY_EXTERNAL_USER_ID = "useRetrievePlayerByExternalUserId";
export const USE_RETRIEVE_TEAMS_BY_PLAYER_ID = "useRetrieveTeamsByPlayerId";
export const useRetrieveTeam = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_TEAM, id],
        queryFn: async () => {
            const response = await api.get(`/teams/${id}/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: !!id && enabled,
    });
};
export const useRetrievePlayersByTeamId = ({ teamId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_PLAYERS_BY_TEAM_ID, teamId],
        queryFn: async () => {
            const response = await api.get(`/teams/${teamId}/players/`);
            return response.data;
        },
        enabled: !!teamId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
// Fetch several teams' rosters in one hook (LS-514 gameweek results, which needs
// every team in a round). Returns a teamId→players map plus aggregate flags.
export const useRetrievePlayersByTeamIds = ({ teamIds, enabled = true, }) => {
    const results = useQueries({
        queries: teamIds.map((teamId) => ({
            queryKey: [USE_LIST_PLAYERS_BY_TEAM_ID, teamId],
            queryFn: async () => {
                const response = await api.get(`/teams/${teamId}/players/`);
                return response.data;
            },
            enabled: !!teamId && enabled,
            staleTime: STALE_TIME,
            cacheTime: CACHE_TIME,
        })),
    });
    const rostersByTeamId = {};
    teamIds.forEach((teamId, i) => {
        rostersByTeamId[teamId] = results[i]?.data ?? [];
    });
    return {
        rostersByTeamId,
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
    };
};
export const useRetrieveUserTeamPermissions = ({ teamId, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_USER_TEAM_PERMISSIONS, teamId],
        queryFn: async () => {
            const response = await api.get(`/permissions/teams/${teamId}/`);
            return response.data;
        },
        enabled: !!teamId,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useSetAssistantManagers = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (userIds) => {
            if (!teamId) {
                throw new Error("Team ID must be defined");
            }
            return api.put(`/teams/${teamId}/assistant-managers/`, { userIds });
        },
        onSuccess: () => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM, teamId]);
            queryClient.invalidateQueries([
                USE_RETRIEVE_USER_TEAM_PERMISSIONS,
                teamId,
            ]);
            onSuccess();
        },
        onError,
    });
};
export const useRetrieveUserPlayerPermissions = ({ playerId, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_USER_PLAYER_PERMISSIONS, playerId],
        queryFn: async () => {
            const response = await api.get(`/permissions/players/${playerId}/`);
            return response.data;
        },
        enabled: !!playerId,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrievePlayerByUserId = ({ userId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_PLAYER_BY_USER_ID, userId],
        queryFn: async () => {
            const response = await api.get(`/users/${userId}/player/`);
            return response.data;
        },
        enabled: !!userId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrievePlayerByExternalUserId = ({ externalUserId, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_PLAYER_BY_EXTERNAL_USER_ID, { externalUserId }],
        queryFn: async () => {
            const response = await api.get(`/external-users/${externalUserId}/player/`);
            return response.data;
        },
        enabled: !!externalUserId,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useUpdateUserPrivacySettings = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.put(`/privacy-settings/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_USER_PRIVACY_CONTROLS]);
            onSuccess(data);
        },
        onError,
    });
};
export const useRetrieveUserPrivacySettings = () => {
    return useQuery({
        queryKey: [USE_RETRIEVE_USER_PRIVACY_CONTROLS],
        queryFn: async () => {
            const response = await api.get(`/privacy-settings/`);
            return response.data;
        },
    });
};
export const useRetrieveTeamsByPlayerId = ({ playerId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_TEAMS_BY_PLAYER_ID, { playerId }],
        queryFn: async () => {
            const response = await api.get(`/players/${playerId}/teams/`);
            return response.data;
        },
        enabled: !!playerId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrievePlayer = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_PLAYER, id],
        queryFn: async () => {
            return (await api.get(`/players/${id}/`))
                .data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: 1000 * 60 * 2,
    });
};
export const useRetrieveIntraSquadMatch = ({ id, enabled, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_INTRA_SQUAD_MATCH, id],
        queryFn: async () => {
            return (await api.get(`/intra-squad-matches/${id}/`, {}))
                .data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useSearchTeams = ({ searchTerm, page = 1, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_SEARCH_TEAMS, searchTerm, page],
        queryFn: async () => {
            const response = await api.get("/search/teams/", { params: { page, q: searchTerm } });
            return response.data;
        },
        enabled: enabled && searchTerm !== undefined,
    });
};
export const useSearchPlayers = ({ searchTerm, page = 1, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_SEARCH_PLAYERS, searchTerm, page],
        queryFn: async () => {
            const response = await api.get("/search/players/", {
                params: {
                    page,
                    q: searchTerm,
                },
            });
            return response.data;
        },
        enabled: enabled && searchTerm !== undefined,
    });
};
export const useRetrieveTeamPreferences = ({ teamId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_TEAM_PREFERENCES, teamId],
        queryFn: async () => {
            const { data } = await api.get(`/teams/${teamId}/preferences/`);
            return data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: !!teamId && enabled,
    });
};
export const useCreateAndAddExternalUserToTeam = ({ teamId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post(`/teams/${teamId}/create-and-add-player/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM, teamId]);
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useRetrieveAddablePlayers = ({ teamId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_ADDABLE_PLAYERS, teamId],
        queryFn: async () => {
            const { data } = await api.get(`/teams/${teamId}/players/addable/`);
            return data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: !!teamId && enabled,
    });
};
export const useAddExistingPlayersToTeam = ({ teamId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ players, }) => {
            return api.post(`/teams/${teamId}/players/add-existing/`, { players });
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM, teamId]);
            queryClient.invalidateQueries([USE_LIST_ADDABLE_PLAYERS, teamId]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdatePlayerMembership = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ playerId, squadNumber, }) => api.patch(`/teams/${teamId}/players/${playerId}/membership/`, {
            squadNumber,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            onSuccess();
        },
        onError,
    });
};
export const useDeactivatePlayer = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ playerId }) => {
            return api.post(`/teams/${teamId}/players/${playerId}/deactivate/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            onSuccess();
        },
        onError,
    });
};
export const useActivatePlayer = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ playerId }) => {
            return api.post(`/teams/${teamId}/players/${playerId}/activate/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            onSuccess();
        },
        onError,
    });
};
export const useRemovePlayerFromTeam = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ playerId }) => {
            return api.delete(`/teams/${teamId}/players/${playerId}/remove/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM, teamId]);
            queryClient.invalidateQueries([USE_LIST_ADDABLE_PLAYERS, teamId]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            onSuccess();
        },
        onError,
    });
};
export const useMergeTeamPlayers = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ sourcePlayerId, targetPlayerId, }) => {
            return api.post(`/teams/${teamId}/players/merge/`, {
                sourcePlayerId,
                targetPlayerId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM, teamId]);
            queryClient.invalidateQueries([USE_LIST_ADDABLE_PLAYERS, teamId]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            onSuccess();
        },
        onError,
    });
};
export const useDeleteUser = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.delete(`/auth/delete-account/`);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries();
            onSuccess(data);
        },
        onError,
    });
};
export const useCreateTeam = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post("/teams/", data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            // Invalidate addable teams queries since team ownership affects what teams are addable
            queryClient.invalidateQueries({ queryKey: [USE_RETRIEVE_ADDABLE_TEAMS] });
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdateTeam = ({ id, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!id) {
                throw new Error("Team ID must be defined");
            }
            return api.put(`/teams/${id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM, id]);
            queryClient.invalidateQueries([USE_RETRIEVE_USER_TEAM_PERMISSIONS, id]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, id]);
            // Invalidate addable teams queries since team changes affect what teams are addable
            queryClient.invalidateQueries({ queryKey: [USE_RETRIEVE_ADDABLE_TEAMS] });
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdateTeamPreference = ({ teamId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!teamId) {
                throw new Error("Team ID must be defined");
            }
            return await api.put(`/teams/${teamId}/preferences/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_PREFERENCES, teamId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useDeletePlayer = ({ id, teamIds, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.delete(`/players/${id}/`);
        },
        onMutate,
        onSuccess: (data) => {
            teamIds.forEach((teamId) => {
                queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, teamId]);
            });
            onSuccess(data);
        },
        onError,
    });
};
export const useClaimTeamOwnership = ({ id, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            if (!id) {
                throw new Error("Team id is required to claim ownership");
            }
            return api.post(`/teams/${id}/claim-ownership/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM, id]);
            queryClient.invalidateQueries([USE_RETRIEVE_USER_TEAM_PERMISSIONS, id]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            onSuccess();
        },
        onError,
    });
};
export const useDeleteTeam = ({ id, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.delete(`/teams/${id}/`);
        },
        onMutate,
        onSuccess: (data) => {
            // Remove cached data for the deleted team — don't refetch (it no longer exists)
            queryClient.removeQueries([USE_RETRIEVE_TEAM, id]);
            queryClient.removeQueries([USE_RETRIEVE_USER_TEAM_PERMISSIONS, id]);
            // Mark teams list as stale — the destination page will refetch on mount
            queryClient.invalidateQueries({
                queryKey: [USE_RETRIEVE_TEAMS_BY_PLAYER_ID],
                refetchType: "none",
            });
            queryClient.invalidateQueries({
                queryKey: [USE_RETRIEVE_ADDABLE_TEAMS],
                refetchType: "none",
            });
            onSuccess(data);
        },
        onError,
    });
};
export const useLogout = (onMutate, onSuccess, onError) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return djRestAuth.post("/logout/", {
                token: "",
                refresh: "",
            });
        },
        onMutate,
        onSuccess: (data) => {
            onSuccess(data);
            // Wipe all cached data so the next user to sign in on this device never
            // sees the previous user's teams, players, etc. (invalidate only marks
            // stale and leaves inactive queries cached).
            queryClient.clear();
        },
        onError,
    });
};
export const useCreateIntraSquadMatch = ({ team, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post("/intra-squad-matches/", data);
        },
        onMutate,
        onSuccess: (data) => {
            if (team === undefined) {
                throw new Error("Team must be defined!");
            }
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_FEED, team.id]);
            team.players?.forEach((playerId) => {
                queryClient.invalidateQueries([USE_RETRIEVE_PLAYER_FEED, playerId]);
            });
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdateIntraSquadMatch = ({ id, team, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!id) {
                throw new Error("Intra-Squad Match ID is not defined!");
            }
            return api.put(`/intra-squad-matches/${id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            if (team === undefined) {
                throw new Error("Team must be defined!");
            }
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_FEED, team.id]);
            team.players?.forEach((playerId) => {
                queryClient.invalidateQueries([USE_RETRIEVE_PLAYER_FEED, playerId]);
            });
            queryClient.invalidateQueries([USE_RETRIEVE_INTRA_SQUAD_MATCH, id]);
            onSuccess(data);
        },
        onError,
    });
};
export const useSubmitIntraSquadMatchStats = (intraSquadMatch) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!intraSquadMatch?.id) {
                throw new Error("Intra-squad match must be defined!");
            }
            return api.post(`/intra-squad-matches/${intraSquadMatch.id}/submit-stats/`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([
                USE_RETRIEVE_TEAM_FEED,
                intraSquadMatch.team,
            ]);
            [
                ...intraSquadMatch.teamAPlayers,
                ...intraSquadMatch.teamBPlayers,
            ].forEach((player) => {
                queryClient.invalidateQueries([USE_RETRIEVE_PLAYER_FEED, player.id]);
            });
            queryClient.invalidateQueries([
                USE_RETRIEVE_INTRA_SQUAD_MATCH,
                intraSquadMatch.id,
            ]);
        },
    });
};
export const useUpdatePlayer = ({ id, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!id) {
                throw new Error("Must define Player Id");
            }
            return api.put(`/players/${id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_PLAYER, id]);
            const userId = data.data.user?.pk;
            if (userId) {
                queryClient.invalidateQueries([USE_RETRIEVE_PLAYER_BY_USER_ID, userId]);
            }
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdateExternalUser = ({ player, playerTeams, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            if (!player) {
                throw new Error("Must define player!");
            }
            if (!player.externalUser) {
                throw new Error("Must define external user!");
            }
            return api.put(`/external-users/${player.externalUser.id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_PLAYER, player.id]);
            queryClient.invalidateQueries([
                USE_RETRIEVE_PLAYER_BY_EXTERNAL_USER_ID,
                player.externalUser.id,
            ]);
            playerTeams.forEach((team) => {
                queryClient.invalidateQueries([USE_LIST_PLAYERS_BY_TEAM_ID, team.id]);
            });
            onSuccess(data);
        },
        onError,
    });
};
